import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getBlogs } from '../../services/public/blogService';
import { 
    formatBlogDate, 
    calculateReadTime, 
    getAuthorInitials,
    getBlogGradient,
    getBlogIcon,
    getBlogTagColor,
    cleanHtmlContent
} from '../../utils/blogDataTransform';

const BlogListing = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalBlogs: 0,
        hasNextPage: false,
        hasPrevPage: false,
        limit: 9
    });
    
    const abortControllerRef = useRef(null);

    // Fetch blogs
    const fetchBlogs = async (page = 1, isLoadMore = false) => {
        // Cancel previous request if any
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();

        try {
            if (isLoadMore) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }
            
            setError(null);

            const response = await getBlogs(page, pagination.limit);
            
            if (response.success) {
                if (isLoadMore) {
                    // Append new blogs to existing list
                    setBlogs(prev => [...prev, ...response.data]);
                } else {
                    // Replace blogs for new search/initial load
                    setBlogs(response.data);
                }
                
                setPagination(response.pagination);
            } else {
                throw new Error(response.message || 'Failed to fetch blogs');
            }
        } catch (err) {
            if (err.code !== 'ERR_CANCELED' && err.name !== 'AbortError' && err.name !== 'CanceledError') {
                console.error('Error fetching blogs:', err);
                setError('Failed to load blogs. Please try again.');
                
                if (!isLoadMore) {
                    setBlogs([]);
                }
            }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchBlogs(1, false);

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // Load more handler
    const handleLoadMore = () => {
        if (pagination.hasNextPage && !loadingMore) {
            fetchBlogs(pagination.currentPage + 1, true);
        }
    };

    // Retry handler
    const handleRetry = () => {
        fetchBlogs(1, false);
    };

    return (
        <div className="container mx-auto px-4 md:px-6 pt-24 pb-16">
            {/* SEO Settings */}
            <title>{`Career Advice, Job Tips & Hiring Insights | ${process.env.REACT_APP_NAME} Blog`}</title>
            <meta
                name="description"
                content="Read career advice, job search tips, and industry insights on the Hunar Pool blog to help you grow professionally and make better career decisions."
            />

            {/* Hero Section */}
            <div className="text-center mb-16">
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                    Hunar Pool <span className="bg-gradient-to-r from-[#0096C7] to-[#4361EE] bg-clip-text text-transparent">Blog</span>
                </h1>
                <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                    Career advice, job search strategies, AI recruitment insights, and
                    industry trends to help you succeed in your professional journey.
                </p>
            </div>

            {/* Error State */}
            {error && !loading && blogs.length === 0 && (
                <div className="text-center py-12">
                    <div className="bg-red-900/20 border border-red-700 rounded-xl p-8 max-w-lg mx-auto">
                        <i className="fas fa-exclamation-triangle text-red-500 text-4xl mb-4"></i>
                        <h3 className="text-xl font-medium text-white mb-2">Something went wrong</h3>
                        <p className="text-gray-400 mb-6">{error}</p>
                        <button 
                            onClick={handleRetry}
                            className="bg-gradient-to-r from-[#0096C7] to-[#4361EE] text-white px-6 py-3 rounded-xl hover:shadow-lg hover:shadow-[#0096C7]/20 transition-all duration-300"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            )}

            {/* Blog Posts Grid */}
            {loading && blogs.length === 0 ? (
                <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0096C7] mx-auto mb-4"></div>
                    <p className="text-gray-400">Loading articles...</p>
                </div>
            ) : (
                <>
                    {blogs.length > 0 ? (
                        <div className="mb-12">
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {blogs.map((blog, index) => {
                                    const readTime = calculateReadTime(blog.fullDescription);
                                    const gradient = getBlogGradient(blog.tags?.[0], index);
                                    const icon = getBlogIcon(blog.tags?.[0]);
                                    const tagColor = getBlogTagColor(blog.tags?.[0]);
                                    //const formattedDate = formatBlogDate(blog.createdAt);
                                    //const authorInitials = getAuthorInitials(blog.createdBy?.name);
                                    
                                    return (
                                        <div 
                                            key={blog._id} 
                                            className="bg-gray-900 rounded-2xl overflow-hidden border border-gray-800 hover:border-gray-700 transition-all duration-300 hover:shadow-lg hover:shadow-[#0096C7]/5"
                                        >
                                            {/* Featured Image or Icon */}
                                            <Link to={`/blog/${blog.slug}`} className="block">
                                                <div className="h-48 bg-gradient-to-br relative overflow-hidden">
                                                    {blog.featuredImage ? (
                                                        <img 
                                                            src={blog.featuredImage} 
                                                            alt={blog.title}
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.style.display = 'none';
                                                                e.target.parentNode.innerHTML = `<div class="w-full h-full bg-gradient-to-br ${gradient} flex items-center justify-center"><i class="fas ${icon} text-5xl opacity-70" style="color: ${tagColor}"></i></div>`;
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className={`w-full h-full bg-gradient-to-br ${gradient} flex items-center justify-center`}>
                                                            <i className={`fas ${icon} text-5xl opacity-70`} style={{ color: tagColor }}></i>
                                                        </div>
                                                    )}
                                                </div>
                                            </Link>
                                            
                                            <div className="p-6">
                                                <div className="flex items-center justify-between mb-4">
                                                    {blog.tags && blog.tags.length > 0 ? (
                                                        <span 
                                                            className="px-3 py-1 bg-gray-800 text-xs rounded-full font-medium"
                                                            style={{ color: tagColor }}
                                                        >
                                                            {blog.tags[0]}
                                                        </span>
                                                    ) : (
                                                        <span className="px-3 py-1 bg-gray-800 text-gray-400 text-xs rounded-full font-medium">
                                                            Article
                                                        </span>
                                                    )}
                                                    <span className="text-gray-400 text-sm">
                                                        <i className="far fa-clock mr-1" /> {readTime}
                                                    </span>
                                                </div>
                                                
                                                <Link to={`/blog/${blog.slug}`}>
                                                    <h3 className="text-xl font-bold mb-3 hover:text-[#0096C7] transition-colors line-clamp-2">
                                                        {blog.title}
                                                    </h3>
                                                </Link>
                                                
                                                <p className="text-gray-300 text-sm mb-4 line-clamp-3">
                                                    {blog.description}
                                                </p>
                                                
                                                <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                                                    {/* <div className="flex items-center">
                                                        <div className="w-8 h-8 rounded-full bg-gray-800 flex items-center justify-center mr-2">
                                                            <span className="text-xs font-bold text-gray-300">
                                                                {authorInitials}
                                                            </span>
                                                        </div>
                                                        <span className="text-sm text-gray-400">
                                                            {blog.createdBy?.name || 'Hunar Pool'}
                                                        </span>
                                                    </div> */}
                                                    <div className="flex items-center space-x-3 mx-auto">    
                                                        <Link 
                                                            to={`/blog/${blog.slug}`}
                                                            className="text-[#0096C7] hover:text-[#00a8e0] text-sm font-medium"
                                                        >
                                                            Read full blog <i className="fas fa-arrow-right ml-1" />
                                                        </Link>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        !loading && !error && (
                            <div className="text-center py-12">
                                <i className="fas fa-newspaper text-gray-500 text-4xl mb-4"></i>
                                <h3 className="text-xl font-bold text-white mb-2">No Articles Found</h3>
                                <p className="text-gray-400">Check back later for new content</p>
                            </div>
                        )
                    )}
                </>
            )}

            {/* Load More Button */}
            {pagination.hasNextPage && (
                <div className="flex justify-center mt-12">
                    <button
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        className={`bg-gray-800 border border-gray-700 text-white px-8 py-4 rounded-xl font-medium flex items-center justify-center transition-all duration-300 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed min-w-[200px]`}
                    >
                        {loadingMore ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                Loading...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-plus-circle mr-2"></i>
                                Load More Articles
                            </>
                        )}
                    </button>
                </div>
            )}

            {/* Loading More Indicator (when already showing blogs) */}
            {loadingMore && blogs.length > 0 && (
                <div className="flex justify-center mt-8">
                    <div className="flex items-center text-gray-400">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#0096C7] mr-3"></div>
                        Loading more articles...
                    </div>
                </div>
            )}
        </div>
    );
};

export default BlogListing;